import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Bold, Italic, List, ListOrdered, Link2, Undo2, Redo2, Heading2, Quote } from 'lucide-react';

/**
 * WYSIWYG editor voor e-mail body. Sluit rechtstreeks aan op TipTap's HTML output.
 * Props:
 *   value:    string (HTML)
 *   onChange: (html: string) => void
 *   placeholder: string
 */
export const EmailEditor = ({ value, onChange, placeholder = 'Typ hier je e-mailtekst…' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#f26b1f] underline' } }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[240px] px-4 py-3 focus:outline-none',
      },
    },
  });

  // Keep editor in sync when parent forces a new value (e.g. loading a different template)
  useEffect(() => {
    if (!editor) return;
    if (value === undefined || value === null) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return <div className="border border-slate-200 rounded-lg h-64 bg-slate-50" />;

  const btn = (active, onClick, Icon, title, testid) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-2 rounded hover:bg-slate-200 ${active ? 'bg-slate-200 text-[#1e1b4b]' : 'text-slate-600'}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  const promptLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('Link URL', prev || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid="email-editor">
      <div className="flex items-center gap-1 border-b border-slate-200 px-2 py-1.5 bg-slate-50">
        {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), Bold, 'Vet', 'ed-bold')}
        {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), Italic, 'Cursief', 'ed-italic')}
        {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, 'Kop', 'ed-h2')}
        <div className="w-px h-5 bg-slate-300 mx-1" />
        {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), List, 'Bulletlijst', 'ed-ul')}
        {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, 'Genummerde lijst', 'ed-ol')}
        {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), Quote, 'Citaat', 'ed-quote')}
        {btn(editor.isActive('link'), promptLink, Link2, 'Link', 'ed-link')}
        <div className="w-px h-5 bg-slate-300 mx-1" />
        {btn(false, () => editor.chain().focus().undo().run(), Undo2, 'Ongedaan', 'ed-undo')}
        {btn(false, () => editor.chain().focus().redo().run(), Redo2, 'Opnieuw', 'ed-redo')}
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
};

export const insertPlaceholderIntoText = (text, placeholder, cursorPos) => {
  const token = `{${placeholder}}`;
  if (typeof cursorPos !== 'number') return (text || '') + token;
  return (text || '').slice(0, cursorPos) + token + (text || '').slice(cursorPos);
};

export default EmailEditor;
